import { useEffect, useState } from "react";
import { useErpConversions, useSaveErpConversions, type ErpConversionItem } from "../api/hooks";
import { Button, Card, ErrorNotice, Input, Notice, PageHeader, Spinner } from "../components/ui";
import {
  CONVERSION_LABELS,
  CONVERSION_SECTIONS,
  changedConversions,
  validateConversionCode,
} from "../lib/erp-export";

/**
 * Tablas de conversión a códigos del ERP. Solo super-admin.
 *
 * Todos los códigos son opcionales: en blanco se exporta el campo vacío. La plataforma no comprueba que el
 * código exista en el ERP (eso solo lo sabe el ERP), solo que respete la longitud del campo.
 */
export function ErpConversionsPage() {
  const query = useErpConversions();
  const save = useSaveErpConversions();
  const [draft, setDraft] = useState<ErpConversionItem[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setDraft(query.data.map((item) => ({ ...item })));
  }, [query.data]);

  const changed = query.data ? changedConversions(query.data, draft) : [];
  const errors = new Map(
    draft.flatMap((item) => {
      const error = validateConversionCode(item);
      return error ? [[`${item.kind}:${item.key}`, error] as const] : [];
    }),
  );

  function setCode(target: ErpConversionItem, value: string): void {
    setSaved(false);
    setDraft((rows) =>
      rows.map((row) => (row.kind === target.kind && row.key === target.key ? { ...row, erpCode: value } : row)),
    );
  }

  async function handleSave(): Promise<void> {
    setSaved(false);
    await save.mutateAsync(changed);
    setSaved(true);
  }

  return (
    <div>
      <PageHeader
        eyebrow="Facturación"
        title="Tablas de conversión"
        description="Códigos del ERP que se escriben en los ficheros de exportación. Todos son opcionales: en blanco, el campo sale vacío."
        actions={
          <Button variant="primary" disabled={changed.length === 0 || errors.size > 0 || save.isPending} onClick={() => void handleSave()}>
            {save.isPending ? "Guardando…" : `Guardar cambios${changed.length > 0 ? ` (${changed.length})` : ""}`}
          </Button>
        }
      />

      {query.isLoading ? <Spinner /> : null}
      {query.isError ? <ErrorNotice error={query.error} onRetry={() => void query.refetch()} /> : null}
      {save.isError ? <ErrorNotice error={save.error} /> : null}
      {saved && changed.length === 0 ? (
        <div className="mb-4">
          <Notice tone="ok">Cambios guardados.</Notice>
        </div>
      ) : null}

      {query.data
        ? CONVERSION_SECTIONS.map((section) => (
            <Card key={section.kind} className="mb-5">
              <h2 className="text-base font-semibold text-ink-900 dark:text-ink-50">{section.title}</h2>
              <p className="mb-3 mt-1 text-sm text-ink-600 dark:text-ink-300">{section.hint}</p>
              <div className="space-y-3">
                {draft
                  .filter((item) => item.kind === section.kind)
                  .map((item) => {
                    const error = errors.get(`${item.kind}:${item.key}`);
                    const id = `conv-${item.kind}-${item.key}`;
                    return (
                      <div key={id} className="grid items-start gap-2 sm:grid-cols-[1fr_12rem]">
                        <label htmlFor={id} className="pt-2 text-sm text-ink-800 dark:text-ink-100">
                          {CONVERSION_LABELS[item.kind][item.key] ?? item.key}
                        </label>
                        <div>
                          <Input
                            id={id}
                            value={item.erpCode}
                            onChange={(event) => setCode(item, event.target.value)}
                            aria-invalid={error ? true : undefined}
                            placeholder="(en blanco)"
                          />
                          {error ? (
                            <p role="alert" className="mt-1 text-xs font-medium text-signal-danger dark:text-red-300">
                              {error}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Card>
          ))
        : null}
    </div>
  );
}
